/**
 * Message Invaders — pure game engine.
 * No React dependency. Operates on a canvas context + callbacks.
 */

import {
  PLAYER,
  CHURN_MONSTER,
  UNSUB_GREMLIN,
  BOUNCE_DEMON,
  SPAM_SPIDER,
  BULLET_EMAIL,
  BULLET_PUSH,
  BULLET_INAPP,
  ENEMY_BOLT,
  POWERUP_BROADCAST,
  POWERUP_SEGMENT,
  POWERUP_ABTEST,
  POWERUP_SHIELD,
  EXPLOSION_1,
  EXPLOSION_2,
  EXPLOSION_3,
  drawSprite,
  spriteSize,
} from './sprites';

// ── Constants ─────────────────────────────────────────────────────
export const CANVAS_W = 480;
export const CANVAS_H = 680;
const SPRITE_SCALE = 3;
const PLAYER_SPEED = 6;
const BULLET_SPEED = 8;
const ENEMY_BULLET_SPEED = 2;
const POWERUP_DROP_SPEED = 2;
const POWERUP_DROP_CHANCE = 0.2;
const ENEMY_SHOOT_BASE = 0.001;
const MAX_LIVES = 3;
const INVULN_DURATION = 2000; // ms after getting hit
const SHIELD_DURATION = 5000;
const ABTEST_DURATION = 6000;

// ── Types ─────────────────────────────────────────────────────────
type EnemyType = 'churn' | 'unsub' | 'bounce' | 'spam';
type PowerupType = 'broadcast' | 'segment' | 'abtest' | 'shield';
type BulletType = 'email' | 'push' | 'inapp';
type GameScreen = 'title' | 'playing' | 'gameover' | 'waveIntro';

interface Entity {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Enemy extends Entity {
  type: EnemyType;
  hp: number;
  maxHp: number;
  points: number;
  flash: number;
  row: number;
  col: number;
}

interface Bullet extends Entity {
  type: BulletType;
  dy: number;
}

interface EnemyBullet extends Entity {
  dy: number;
}

interface Powerup extends Entity {
  type: PowerupType;
  dy: number;
}

interface Explosion {
  x: number;
  y: number;
  frame: number;
  timer: number;
}

interface Star {
  x: number;
  y: number;
  speed: number;
  bright: boolean;
  blink: number;
}

export interface GameState {
  screen: GameScreen;
  score: number;
  highScore: number;
  isNewHighScore: boolean;
  lives: number;
  wave: number;
  waveIntroTimer: number;
  onGameOver?: (score: number, wave: number, isNewHighScore: boolean) => void;

  // Player
  player: Entity;
  playerInvuln: number;

  // Active effects
  shieldTimer: number;
  abtestTimer: number;

  // Enemies
  enemies: Enemy[];
  enemyDx: number;
  enemySpeed: number;
  enemyMoveTimer: number;
  enemyMoveInterval: number;

  // Projectiles
  bullets: Bullet[];
  enemyBullets: EnemyBullet[];
  lastShotTime: number;

  // Powerups
  powerups: Powerup[];

  // Effects
  explosions: Explosion[];
  stars: Star[];
  screenShake: number;
  flashMessage: string;
  flashTimer: number;

  // Combo
  combo: number;
  comboTimer: number;

  // Input
  keys: Set<string>;

  // Timing
  lastTime: number;
  frameId: number;
  paused: boolean;
}

const ENEMY_SPRITES: Record<EnemyType, string[]> = {
  churn: CHURN_MONSTER,
  unsub: UNSUB_GREMLIN,
  bounce: BOUNCE_DEMON,
  spam: SPAM_SPIDER,
};

const ENEMY_CONFIG: Record<EnemyType, { hp: number; points: number }> = {
  churn: { hp: 1, points: 100 },
  unsub: { hp: 1, points: 150 },
  bounce: { hp: 2, points: 200 },
  spam: { hp: 2, points: 250 },
};

const BULLET_SPRITES: Record<BulletType, string[]> = {
  email: BULLET_EMAIL,
  push: BULLET_PUSH,
  inapp: BULLET_INAPP,
};

const POWERUP_SPRITES: Record<PowerupType, string[]> = {
  broadcast: POWERUP_BROADCAST,
  segment: POWERUP_SEGMENT,
  abtest: POWERUP_ABTEST,
  shield: POWERUP_SHIELD,
};

const WAVE_NAMES: string[] = [
  'The Onboarding',
  'The Re-engagement',
  'The Win-back',
  'The Retention Blitz',
  'The Sunset Campaign',
  'The Last Resort',
  'The Loyalty Push',
  'The Final Stand',
];

// ── Helpers ───────────────────────────────────────────────────────
function boxCollide(a: Entity, b: Entity): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function randomPowerup(): PowerupType {
  const types: PowerupType[] = ['broadcast', 'segment', 'abtest', 'shield'];
  return types[Math.floor(Math.random() * types.length)];
}

function createStars(): Star[] {
  const stars: Star[] = [];
  for (let index = 0; index < 60; index++) {
    stars.push({
      x: Math.random() * CANVAS_W,
      y: Math.random() * CANVAS_H,
      speed: 0.2 + Math.random() * 0.6,
      bright: Math.random() > 0.5,
      blink: Math.random() * 200,
    });
  }
  return stars;
}

// ── Wave generation ───────────────────────────────────────────────
function generateWave(wave: number): Enemy[] {
  const enemies: Enemy[] = [];
  const rows = Math.min(2 + Math.floor(wave / 2), 5);
  const cols = Math.min(5 + Math.floor(wave / 3), 9);

  // Row type assignment — harder enemies appear in later waves
  const rowTypes: EnemyType[] = [];
  for (let r = 0; r < rows; r++) {
    if (r === 0) rowTypes.push(wave >= 4 ? 'spam' : wave >= 2 ? 'bounce' : 'churn');
    else if (r === 1) rowTypes.push(wave >= 3 ? 'bounce' : 'unsub');
    else if (r === 2) rowTypes.push(wave >= 2 ? 'unsub' : 'churn');
    else rowTypes.push('churn');
  }

  const { w: ew, h: eh } = spriteSize(CHURN_MONSTER, SPRITE_SCALE);
  const spacingX = ew + 8;
  const spacingY = eh + 8;
  const totalW = cols * spacingX;
  const startX = (CANVAS_W - totalW) / 2;
  const startY = 60;

  for (let r = 0; r < rows; r++) {
    const type = rowTypes[r];
    const cfg = ENEMY_CONFIG[type];
    const sprite = ENEMY_SPRITES[type];
    const { w, h } = spriteSize(sprite, SPRITE_SCALE);
    for (let c = 0; c < cols; c++) {
      enemies.push({
        x: startX + c * spacingX,
        y: startY + r * spacingY,
        w,
        h,
        type,
        hp: cfg.hp,
        maxHp: cfg.hp,
        points: cfg.points,
        flash: 0,
        row: r,
        col: c,
      });
    }
  }

  return enemies;
}

// ── Engine ─────────────────────────────────────────────────────────
export function createGameState(): GameState {
  const { w: pw, h: ph } = spriteSize(PLAYER, SPRITE_SCALE);
  return {
    screen: 'title',
    score: 0,
    highScore: (() => {
      try {
        return Number.parseInt(localStorage.getItem('messageInvadersHigh') ?? '0', 10);
      } catch {
        return 0;
      }
    })(),
    isNewHighScore: false,
    lives: MAX_LIVES,
    wave: 0,
    waveIntroTimer: 0,
    player: { x: CANVAS_W / 2 - pw / 2, y: CANVAS_H - ph - 20, w: pw, h: ph },
    playerInvuln: 0,
    shieldTimer: 0,
    abtestTimer: 0,
    enemies: [],
    enemyDx: 1,
    enemySpeed: 1,
    enemyMoveTimer: 0,
    enemyMoveInterval: 800,

    bullets: [],
    enemyBullets: [],
    lastShotTime: 0,
    powerups: [],
    explosions: [],
    stars: createStars(),
    screenShake: 0,
    flashMessage: '',
    flashTimer: 0,
    combo: 0,
    comboTimer: 0,
    keys: new Set(),
    lastTime: 0,
    frameId: 0,
    paused: false,
  };
}

function startWave(state: GameState) {
  state.wave++;
  state.enemies = generateWave(state.wave);
  state.enemyDx = 1;
  // Gentler scaling: logarithmic curve so late waves don't spike as hard
  state.enemySpeed = 1 + Math.log2(state.wave + 1) * 0.15;
  state.enemyMoveInterval = Math.max(500, 900 - state.wave * 30);
  state.enemyMoveTimer = 0;
  state.enemyBullets = [];
  state.powerups = [];
  state.bullets = [];
  state.screen = 'waveIntro';
  state.waveIntroTimer = 2000;
}

function startGame(state: GameState) {
  const { w: pw, h: ph } = spriteSize(PLAYER, SPRITE_SCALE);
  state.score = 0;
  state.lives = MAX_LIVES;
  state.wave = 0;
  state.player.x = CANVAS_W / 2 - pw / 2;
  state.player.y = CANVAS_H - ph - 20;
  state.playerInvuln = 0;
  state.shieldTimer = 0;
  state.abtestTimer = 0;
  state.bullets = [];
  state.enemyBullets = [];
  state.powerups = [];
  state.explosions = [];
  state.combo = 0;
  state.comboTimer = 0;
  state.flashMessage = '';
  state.flashTimer = 0;
  state.screenShake = 0;
  state.isNewHighScore = false;
  startWave(state);
}

function shootBullet(state: GameState, now: number) {
  const cooldown = state.abtestTimer > 0 ? 120 : 200;
  if (now - state.lastShotTime < cooldown) return;
  state.lastShotTime = now;

  // Cycle bullet types based on wave
  const types: BulletType[] = ['email'];
  if (state.wave >= 2) types.push('push');
  if (state.wave >= 4) types.push('inapp');
  const type = types[Math.floor(Math.random() * types.length)];

  const sprite = BULLET_SPRITES[type];
  const { w: bw, h: bh } = spriteSize(sprite, SPRITE_SCALE);
  const bx = state.player.x + state.player.w / 2 - bw / 2;
  const by = state.player.y - bh;

  state.bullets.push({ x: bx, y: by, w: bw, h: bh, type, dy: -BULLET_SPEED });

  // A/B test fires a second bullet offset
  if (state.abtestTimer > 0) {
    state.bullets.push({
      x: bx + (Math.random() > 0.5 ? 12 : -12),
      y: by + 6,
      w: bw,
      h: bh,
      type,
      dy: -BULLET_SPEED,
    });
  }
}

function applyPowerup(state: GameState, type: PowerupType) {
  switch (type) {
    case 'broadcast':
      // Kill all enemies on screen
      for (const enemy of state.enemies) {
        state.explosions.push({ x: enemy.x, y: enemy.y, frame: 0, timer: 0 });
        state.score += enemy.points;
      }
      state.enemies = [];
      state.screenShake = 8;
      state.flashMessage = '📡 BROADCAST — ALL CLEAR!';
      state.flashTimer = 2000;
      break;
    case 'segment':
      // Kill the bottom row
      if (state.enemies.length > 0) {
        const maxRow = Math.max(...state.enemies.map((enemy) => enemy.row));
        const toKill = state.enemies.filter((enemy) => enemy.row === maxRow);
        for (const enemy of toKill) {
          state.explosions.push({ x: enemy.x, y: enemy.y, frame: 0, timer: 0 });
          state.score += enemy.points;
        }
        state.enemies = state.enemies.filter((enemy) => enemy.row !== maxRow);
        state.screenShake = 8;
        state.flashMessage = '📊 SEGMENT — ROW CLEARED!';
        state.flashTimer = 2000;
      }
      break;
    case 'abtest':
      state.abtestTimer = ABTEST_DURATION;
      state.flashMessage = '🔀 A/B TEST — DOUBLE FIRE!';
      state.flashTimer = 2000;
      break;
    case 'shield':
      state.shieldTimer = SHIELD_DURATION;
      state.flashMessage = '🛡️ DELIVERABILITY SHIELD!';
      state.flashTimer = 2000;
      break;
  }
}

// ── Update ─────────────────────────────────────────────────────────
function update(state: GameState, dt: number, now: number) {
  if (state.screen === 'waveIntro') {
    state.waveIntroTimer -= dt;
    if (state.waveIntroTimer <= 0) {
      state.screen = 'playing';
    }
    updateStars(state, dt);
    return;
  }

  if (state.screen !== 'playing') {
    updateStars(state, dt);
    return;
  }

  // Timers
  if (state.playerInvuln > 0) state.playerInvuln -= dt;
  if (state.shieldTimer > 0) state.shieldTimer -= dt;
  if (state.abtestTimer > 0) state.abtestTimer -= dt;
  if (state.flashTimer > 0) state.flashTimer -= dt;
  if (state.screenShake > 0) state.screenShake -= dt * 0.06;
  if (state.comboTimer > 0) {
    state.comboTimer -= dt;
    if (state.comboTimer <= 0) state.combo = 0;
  }

  // Player movement
  const { keys, player } = state;
  const dtScale = dt / 16; // normalize to ~60fps baseline
  if (keys.has('ArrowLeft') || keys.has('a')) player.x -= PLAYER_SPEED * dtScale;
  if (keys.has('ArrowRight') || keys.has('d')) player.x += PLAYER_SPEED * dtScale;
  player.x = Math.max(0, Math.min(CANVAS_W - player.w, player.x));

  // Shooting
  if (keys.has(' ') || keys.has('ArrowUp') || keys.has('w')) {
    shootBullet(state, now);
  }

  // Update bullets
  for (const b of state.bullets) {
    b.y += b.dy * dtScale;
  }
  state.bullets = state.bullets.filter((b) => b.y + b.h > 0);

  // Update enemy bullets
  for (const b of state.enemyBullets) {
    b.y += b.dy * dtScale;
  }
  state.enemyBullets = state.enemyBullets.filter((b) => b.y < CANVAS_H);

  // Update powerups
  for (const p of state.powerups) {
    p.y += p.dy * dtScale;
  }
  state.powerups = state.powerups.filter((p) => p.y < CANVAS_H);

  // Enemy movement (classic step pattern)
  state.enemyMoveTimer += dt;
  if (state.enemyMoveTimer >= state.enemyMoveInterval / state.enemySpeed) {
    state.enemyMoveTimer = 0;

    // Check bounds
    let hitEdge = false;
    for (const enemy of state.enemies) {
      const nextX = enemy.x + state.enemyDx * 12;
      if (nextX <= 0 || nextX + enemy.w >= CANVAS_W) {
        hitEdge = true;
        break;
      }
    }

    if (hitEdge) {
      // Step down
      for (const enemy of state.enemies) {
        enemy.y += 16;
      }
      state.enemyDx *= -1;
    } else {
      for (const enemy of state.enemies) {
        enemy.x += state.enemyDx * 12;
      }
    }

    // Spam spiders get extra random horizontal jitter
    for (const enemy of state.enemies) {
      if (enemy.type === 'spam') {
        enemy.x += (Math.random() - 0.5) * 8;
      }
    }
  }

  // Enemy shooting
  for (const enemy of state.enemies) {
    const shootChance = ENEMY_SHOOT_BASE * (1 + (state.wave - 1) * 0.5) * state.enemySpeed * dtScale;
    if (Math.random() < shootChance) {
      const { w: bw, h: bh } = spriteSize(ENEMY_BOLT, SPRITE_SCALE);
      state.enemyBullets.push({
        x: enemy.x + enemy.w / 2 - bw / 2,
        y: enemy.y + enemy.h,
        w: bw,
        h: bh,
        dy: ENEMY_BULLET_SPEED + Math.min(state.wave * 0.1, 0.8),
      });
    }
  }

  // Enemy flash decay
  for (const enemy of state.enemies) {
    if (enemy.flash > 0) enemy.flash -= dt;
  }

  // Collision: bullets → enemies
  const bulletsToRemove = new Set<Bullet>();
  const enemiesToRemove = new Set<Enemy>();

  for (const bullet of state.bullets) {
    for (const enemy of state.enemies) {
      if (enemiesToRemove.has(enemy)) continue;
      if (boxCollide(bullet, enemy)) {
        bulletsToRemove.add(bullet);
        enemy.hp--;
        enemy.flash = 100;
        if (enemy.hp <= 0) {
          enemiesToRemove.add(enemy);
          state.explosions.push({ x: enemy.x, y: enemy.y, frame: 0, timer: 0 });

          // Combo scoring
          state.combo++;
          state.comboTimer = 2000;
          const comboMultiplier = Math.min(state.combo, 10);
          state.score += enemy.points * comboMultiplier;

          // Powerup drop
          if (Math.random() < POWERUP_DROP_CHANCE) {
            const pType = randomPowerup();
            const sprite = POWERUP_SPRITES[pType];
            const { w: pw, h: ph } = spriteSize(sprite, SPRITE_SCALE);
            state.powerups.push({
              x: enemy.x + enemy.w / 2 - pw / 2,
              y: enemy.y,
              w: pw,
              h: ph,
              type: pType,
              dy: POWERUP_DROP_SPEED,
            });
          }
        }
        break;
      }
    }
  }

  state.bullets = state.bullets.filter((b) => !bulletsToRemove.has(b));
  state.enemies = state.enemies.filter((enemy) => !enemiesToRemove.has(enemy));

  // Collision: enemy bullets → player
  if (state.playerInvuln <= 0) {
    for (const bullet of state.enemyBullets) {
      if (boxCollide(bullet, player)) {
        if (state.shieldTimer > 0) {
          // Shield absorbs hit
          state.shieldTimer = 0;
          state.flashMessage = '🛡️ SHIELD ABSORBED HIT!';
          state.flashTimer = 1500;
          state.screenShake = 3;
        } else {
          state.lives--;
          state.playerInvuln = INVULN_DURATION;
          state.screenShake = 6;
          state.combo = 0;
          if (state.lives <= 0) {
            gameOver(state);
            return;
          }
        }
        state.enemyBullets = state.enemyBullets.filter((b) => b !== bullet);
        break;
      }
    }
  }

  // Collision: enemies reaching player level
  for (const enemy of state.enemies) {
    if (enemy.y + enemy.h >= player.y) {
      gameOver(state);
      return;
    }
  }

  // Collision: powerups → player
  for (const powerup of state.powerups) {
    if (boxCollide(powerup, player)) {
      applyPowerup(state, powerup.type);
      state.powerups = state.powerups.filter((p) => p !== powerup);
    }
  }

  // Update explosions
  for (const exp of state.explosions) {
    exp.timer += dt;
    if (exp.timer > 80) {
      exp.frame++;
      exp.timer = 0;
    }
  }
  state.explosions = state.explosions.filter((exp) => exp.frame < 3);

  // Wave cleared? Wait for flash message to finish before advancing
  if (state.enemies.length === 0 && state.screen === 'playing' && state.flashTimer <= 0) {
    startWave(state);
  }

  updateStars(state, dt);
}

function updateStars(state: GameState, dt: number) {
  for (const star of state.stars) {
    star.y += star.speed * (dt / 16);
    star.blink -= dt;
    if (star.blink <= 0) {
      star.bright = !star.bright;
      star.blink = 100 + Math.random() * 300;
    }
    if (star.y > CANVAS_H) {
      star.y = 0;
      star.x = Math.random() * CANVAS_W;
    }
  }
}

function gameOver(state: GameState) {
  state.screen = 'gameover';
  state.isNewHighScore = state.score > state.highScore && state.score > 0;
  if (state.score > state.highScore) {
    state.highScore = state.score;
    try {
      localStorage.setItem('messageInvadersHigh', String(state.score));
    } catch {
      // localStorage may be unavailable in privacy/incognito modes
    }
  }
  state.onGameOver?.(state.score, state.wave, state.isNewHighScore);
}

// ── Render ─────────────────────────────────────────────────────────
function render(context: CanvasRenderingContext2D, state: GameState, now: number) {
  context.save();

  // Screen shake
  if (state.screenShake > 0) {
    const sx = (Math.random() - 0.5) * state.screenShake;
    const sy = (Math.random() - 0.5) * state.screenShake;
    context.translate(sx, sy);
  }

  // Background
  context.fillStyle = '#0a0a1a';
  context.fillRect(-10, -10, CANVAS_W + 20, CANVAS_H + 20);

  // Stars
  for (const star of state.stars) {
    context.fillStyle = star.bright ? '#ffffff' : '#555566';
    context.fillRect(star.x, star.y, 2, 2);
  }

  if (state.screen === 'title') {
    renderTitle(context, now);
    context.restore();
    return;
  }

  if (state.screen === 'gameover') {
    renderGameplay(context, state, now);
    renderGameOver(context, state);
    context.restore();
    return;
  }

  if (state.screen === 'waveIntro') {
    renderWaveIntro(context, state, now);
    context.restore();
    return;
  }

  renderGameplay(context, state, now);
  renderHUD(context, state);
  context.restore();
}

function renderGameplay(context: CanvasRenderingContext2D, state: GameState, now: number) {
  // Enemies
  for (const enemy of state.enemies) {
    const sprite = ENEMY_SPRITES[enemy.type];
    drawSprite(context, sprite, enemy.x, enemy.y, SPRITE_SCALE, enemy.flash > 0);

    // HP bar for multi-hit enemies
    if (enemy.maxHp > 1) {
      const barW = enemy.w;
      const barH = 3;
      const barY = enemy.y - 6;
      context.fillStyle = '#333';
      context.fillRect(enemy.x, barY, barW, barH);
      context.fillStyle = enemy.hp > 1 ? '#44dd66' : '#ff4457';
      context.fillRect(enemy.x, barY, barW * (enemy.hp / enemy.maxHp), barH);
    }
  }

  // Enemy bullets
  for (const b of state.enemyBullets) {
    drawSprite(context, ENEMY_BOLT, b.x, b.y, SPRITE_SCALE);
  }

  // Player bullets
  for (const b of state.bullets) {
    drawSprite(context, BULLET_SPRITES[b.type], b.x, b.y, SPRITE_SCALE);
  }

  // Powerups (with pulsing glow)
  for (const p of state.powerups) {
    const pulse = Math.sin(now * 0.005) * 0.3 + 0.7;
    context.globalAlpha = pulse;
    const sprite = POWERUP_SPRITES[p.type];
    drawSprite(context, sprite, p.x, p.y, SPRITE_SCALE);
    context.globalAlpha = 1;
  }

  // Explosions
  const EXPLOSION_FRAMES = [EXPLOSION_1, EXPLOSION_2, EXPLOSION_3];
  for (const exp of state.explosions) {
    if (exp.frame < EXPLOSION_FRAMES.length) {
      drawSprite(context, EXPLOSION_FRAMES[exp.frame], exp.x, exp.y, SPRITE_SCALE);
    }
  }

  // Player
  const blinking = state.playerInvuln > 0 && Math.floor(now / 80) % 2 === 0;
  if (!blinking) {
    drawSprite(context, PLAYER, state.player.x, state.player.y, SPRITE_SCALE);

    // Shield aura
    if (state.shieldTimer > 0) {
      context.strokeStyle = `rgba(68, 153, 255, ${0.4 + Math.sin(now * 0.008) * 0.3})`;
      context.lineWidth = 2;
      context.beginPath();
      context.ellipse(
        state.player.x + state.player.w / 2,
        state.player.y + state.player.h / 2,
        state.player.w / 2 + 8,
        state.player.h / 2 + 8,
        0,
        0,
        Math.PI * 2,
      );
      context.stroke();
    }
  }

  // Flash message
  if (state.flashTimer > 0) {
    const alpha = Math.min(1, state.flashTimer / 500);
    context.globalAlpha = alpha;
    context.font = 'bold 18px monospace';
    context.fillStyle = '#ffdd44';
    context.textAlign = 'center';
    context.fillText(state.flashMessage, CANVAS_W / 2, CANVAS_H / 2 - 40);
    context.globalAlpha = 1;
  }

  // Combo indicator
  if (state.combo > 1) {
    context.font = 'bold 14px monospace';
    context.fillStyle = '#66ffaa';
    context.textAlign = 'right';
    context.fillText(`${state.combo}x COMBO`, CANVAS_W - 16, CANVAS_H - 16);
  }
}

function renderHUD(context: CanvasRenderingContext2D, state: GameState) {
  // Top bar
  context.fillStyle = 'rgba(10, 10, 26, 0.7)';
  context.fillRect(0, 0, CANVAS_W, 40);

  context.font = 'bold 14px monospace';
  context.textAlign = 'left';

  // Score
  context.fillStyle = '#ffffff';
  context.fillText(`SCORE: ${state.score.toLocaleString()}`, 16, 26);

  // Wave
  context.textAlign = 'center';
  context.fillStyle = '#99aabb';
  context.fillText(`WAVE ${state.wave}`, CANVAS_W / 2, 26);

  // Lives
  context.textAlign = 'right';
  context.fillStyle = '#ff4457';
  let livesString = '';
  for (let index = 0; index < state.lives; index++) livesString += '♥ ';
  context.fillText(livesString.trim(), CANVAS_W - 16, 26);

  // Active effects
  let effectY = 54;
  context.font = '11px monospace';
  context.textAlign = 'right';
  if (state.shieldTimer > 0) {
    context.fillStyle = '#4499ff';
    context.fillText(`🛡️ ${Math.ceil(state.shieldTimer / 1000)}s`, CANVAS_W - 16, effectY);
    effectY += 16;
  }
  if (state.abtestTimer > 0) {
    context.fillStyle = '#44dd66';
    context.fillText(`🔀 ${Math.ceil(state.abtestTimer / 1000)}s`, CANVAS_W - 16, effectY);
  }
}

function renderTitle(context: CanvasRenderingContext2D, now: number) {
  // Title
  context.font = 'bold 36px monospace';
  context.textAlign = 'center';
  context.fillStyle = '#ff4457';
  context.fillText('MESSAGE', CANVAS_W / 2, 200);
  context.fillStyle = '#ffdd44';
  context.fillText('INVADERS', CANVAS_W / 2, 245);

  // Subtitle
  context.font = '14px monospace';
  context.fillStyle = '#99aabb';
  context.fillText('Defend your audience from the forces of churn', CANVAS_W / 2, 290);

  // Animated demo enemies
  const demoTypes: EnemyType[] = ['churn', 'unsub', 'bounce', 'spam'];
  const labels = ['CHURN MONSTER', 'UNSUB GREMLIN', 'BOUNCE DEMON', 'SPAM SPIDER'];
  const points = ['100pts', '150pts', '200pts', '250pts'];
  for (let index = 0; index < 4; index++) {
    const sprite = ENEMY_SPRITES[demoTypes[index]];
    const { w } = spriteSize(sprite, 2);
    const baseX = CANVAS_W / 2 - 100;
    const baseY = 330 + index * 44;
    const bobble = Math.sin(now * 0.003 + index) * 3;
    drawSprite(context, sprite, baseX - w / 2, baseY + bobble, 2);
    context.font = '12px monospace';
    context.textAlign = 'left';
    context.fillStyle = '#ffffff';
    context.fillText(labels[index], baseX + 30, baseY + 14);
    context.fillStyle = '#ffdd44';
    context.fillText(points[index], baseX + 30, baseY + 28);
  }

  // Controls
  context.font = '12px monospace';
  context.fillStyle = '#666688';
  context.textAlign = 'center';
  context.fillText('← → or A D to move  •  SPACE to fire', CANVAS_W / 2, 550);

  // Start prompt
  const blink = Math.sin(now * 0.004) > 0;
  if (blink) {
    context.font = 'bold 20px monospace';
    context.fillStyle = '#44ddee';
    context.fillText('PRESS SPACE TO START', CANVAS_W / 2, 600);
  }
}

function renderWaveIntro(context: CanvasRenderingContext2D, state: GameState, now: number) {
  const waveName = WAVE_NAMES[(state.wave - 1) % WAVE_NAMES.length];

  context.font = 'bold 16px monospace';
  context.textAlign = 'center';
  context.fillStyle = '#99aabb';
  context.fillText(`WAVE ${state.wave}`, CANVAS_W / 2, CANVAS_H / 2 - 30);

  context.font = 'bold 28px monospace';
  context.fillStyle = '#ffdd44';
  context.fillText(waveName, CANVAS_W / 2, CANVAS_H / 2 + 10);

  const dots = '.'.repeat(Math.floor((now / 400) % 4));
  context.font = '14px monospace';
  context.fillStyle = '#666688';
  context.fillText(`Incoming${dots}`, CANVAS_W / 2, CANVAS_H / 2 + 50);
}

function renderGameOver(context: CanvasRenderingContext2D, state: GameState) {
  // Overlay
  context.fillStyle = 'rgba(10, 10, 26, 0.8)';
  context.fillRect(0, 0, CANVAS_W, CANVAS_H);

  context.textAlign = 'center';

  context.font = 'bold 32px monospace';
  context.fillStyle = '#ff4457';
  context.fillText('GAME OVER', CANVAS_W / 2, CANVAS_H / 2 - 60);

  context.font = '16px monospace';
  context.fillStyle = '#99aabb';
  context.fillText('Your audience has churned 😢', CANVAS_W / 2, CANVAS_H / 2 - 20);

  context.font = 'bold 20px monospace';
  context.fillStyle = '#ffffff';
  context.fillText(`SCORE: ${state.score.toLocaleString()}`, CANVAS_W / 2, CANVAS_H / 2 + 30);

  if (state.isNewHighScore) {
    context.font = 'bold 14px monospace';
    context.fillStyle = '#ffdd44';
    context.fillText('★ NEW HIGH SCORE ★', CANVAS_W / 2, CANVAS_H / 2 + 55);
  }

  context.font = '14px monospace';
  context.fillStyle = '#44ddee';
  context.fillText(`HIGH SCORE: ${state.highScore.toLocaleString()}`, CANVAS_W / 2, CANVAS_H / 2 + 85);

  context.font = '14px monospace';
  context.fillStyle = '#666688';
  context.fillText(`Reached Wave ${state.wave}`, CANVAS_W / 2, CANVAS_H / 2 + 115);

  context.font = 'bold 16px monospace';
  context.fillStyle = '#44ddee';
  context.fillText('PRESS SPACE TO PLAY AGAIN', CANVAS_W / 2, CANVAS_H / 2 + 160);
}

// ── Game Loop ─────────────────────────────────────────────────────
export function runGameLoop(canvas: HTMLCanvasElement, state: GameState, onScoreChange?: (score: number) => void) {
  const context = canvas.getContext('2d');
  if (context == null) return;

  // Disable image smoothing for crispy pixels
  context.imageSmoothingEnabled = false;

  function loop(timestamp: number) {
    if (state.lastTime === 0) state.lastTime = timestamp;
    const dt = Math.min(timestamp - state.lastTime, 50); // cap delta
    state.lastTime = timestamp;

    if (!state.paused) {
      update(state, dt, timestamp);
      if (onScoreChange != null) onScoreChange(state.score);
    }
    render(context!, state, timestamp);

    state.frameId = requestAnimationFrame(loop);
  }

  state.frameId = requestAnimationFrame(loop);
}

export function stopGameLoop(state: GameState) {
  if (state.frameId !== 0) {
    cancelAnimationFrame(state.frameId);
    state.frameId = 0;
  }
}

export function handleKeyDown(state: GameState, key: string) {
  state.keys.add(key);

  if (key === ' ') {
    if (state.screen === 'title') {
      startGame(state);
    } else if (state.screen === 'gameover') {
      startGame(state);
    }
  }
}

export function handleKeyUp(state: GameState, key: string) {
  state.keys.delete(key);
}
